import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { UserCog, Plus, Trash2, Pencil, Search, Tags, Check, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ============== Sortable Doctor Card ==============
const SortableDoctorCard = ({ doc, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: doc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-[#FAF9FE] rounded-lg border border-slate-100"
      data-testid={`doctor-card-${doc.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-slate-400 hover:text-[#655dc6] cursor-grab active:cursor-grabbing touch-none shrink-0"
        title="اسحب لإعادة الترتيب"
        data-testid={`drag-doctor-${doc.id}`}
        aria-label="drag handle"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#655dc6] to-[#8479d9] flex items-center justify-center text-white font-bold text-sm shrink-0">
        {doc.name.replace("د.", "").replace("بروفيسور", "").trim().charAt(0)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{doc.name}</p>
        <p className="text-xs text-slate-500 truncate">
          {doc.role}{doc.exp ? ` • ${doc.exp}` : ""}
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        <button
          onClick={onEdit}
          className="p-1.5 text-slate-500 hover:text-[#655dc6] hover:bg-white rounded transition-colors"
          data-testid={`edit-doctor-${doc.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-white rounded transition-colors"
          data-testid={`delete-doctor-${doc.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ============== Sortable Specialty Row ==============
const SortableSpecialtyRow = ({
  sp,
  doctorCount,
  isEditing,
  editingName,
  setEditingName,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sp.id, disabled: isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2.5 bg-[#FAF9FE] rounded-lg border border-slate-100"
      data-testid={`specialty-row-${sp.id}`}
    >
      {isEditing ? (
        <>
          <Input
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            className="h-8 text-sm"
            autoFocus
            data-testid={`edit-specialty-input-${sp.id}`}
          />
          <button
            onClick={onSaveEdit}
            className="p-1.5 text-green-600 hover:bg-white rounded transition-colors"
            data-testid={`save-specialty-${sp.id}`}
            title="حفظ"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={onCancelEdit}
            className="p-1.5 text-slate-500 hover:bg-white rounded transition-colors"
            title="إلغاء"
          >
            <X className="w-4 h-4" />
          </button>
        </>
      ) : (
        <>
          <button
            {...attributes}
            {...listeners}
            className="p-1 text-slate-400 hover:text-[#655dc6] cursor-grab active:cursor-grabbing touch-none"
            title="اسحب لإعادة الترتيب"
            data-testid={`drag-specialty-${sp.id}`}
            aria-label="drag handle"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{sp.name}</p>
            <p className="text-xs text-slate-400">{doctorCount} طبيب</p>
          </div>
          <button
            onClick={onStartEdit}
            className="p-1.5 text-slate-500 hover:text-[#655dc6] hover:bg-white rounded transition-colors"
            data-testid={`edit-specialty-${sp.id}`}
            title="تعديل"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-white rounded transition-colors"
            data-testid={`delete-specialty-${sp.id}`}
            title="حذف"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  );
};

const DoctorsManagement = ({ embedded = false }) => {
  const { token } = useAuth();
  const [data, setData] = useState({ specialties: [], doctors: [] });
  const [specialtyDocs, setSpecialtyDocs] = useState([]); // with IDs
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", exp: "", specialty: "" });
  // Specialty management
  const [spDialogOpen, setSpDialogOpen] = useState(false);
  const [newSpName, setNewSpName] = useState("");
  const [editingSpId, setEditingSpId] = useState(null);
  const [editingSpName, setEditingSpName] = useState("");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [docsRes, spRes] = await Promise.all([
        axios.get(`${API}/doctors`),
        axios.get(`${API}/specialties`),
      ]);
      setData(docsRes.data);
      setSpecialtyDocs(spRes.data || []);
    } catch {
      toast.error("فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ===== Specialty CRUD =====
  const addSpecialty = async () => {
    const name = newSpName.trim();
    if (!name) {
      toast.error("اسم التخصص مطلوب");
      return;
    }
    try {
      await axios.post(`${API}/specialties`, { name }, authHeaders);
      toast.success("تمت إضافة التخصص");
      setNewSpName("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل في الإضافة");
    }
  };

  const startEditSp = (sp) => {
    setEditingSpId(sp.id);
    setEditingSpName(sp.name);
  };

  const cancelEditSp = () => {
    setEditingSpId(null);
    setEditingSpName("");
  };

  const saveEditSp = async (sp) => {
    const name = editingSpName.trim();
    if (!name) {
      toast.error("اسم التخصص مطلوب");
      return;
    }
    if (name === sp.name) {
      cancelEditSp();
      return;
    }
    try {
      await axios.put(`${API}/specialties/${sp.id}`, { name }, authHeaders);
      toast.success("تم تعديل التخصص");
      cancelEditSp();
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل في التعديل");
    }
  };

  const deleteSp = async (sp) => {
    if (!window.confirm(`حذف تخصص "${sp.name}"؟`)) return;
    try {
      await axios.delete(`${API}/specialties/${sp.id}`, authHeaders);
      toast.success("تم حذف التخصص");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل في الحذف");
    }
  };

  // Drag-and-drop reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = specialtyDocs.findIndex(s => s.id === active.id);
    const newIndex = specialtyDocs.findIndex(s => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(specialtyDocs, oldIndex, newIndex);
    setSpecialtyDocs(reordered); // optimistic update
    // Also update the order in the main doctors data so groupings update immediately
    setData(prev => ({ ...prev, specialties: reordered.map(s => s.name) }));

    try {
      await axios.post(
        `${API}/specialties/reorder`,
        { ids: reordered.map(s => s.id) },
        authHeaders,
      );
      toast.success("تم حفظ الترتيب");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل حفظ الترتيب");
      fetchAll(); // revert
    }
  };

  // Doctors drag-and-drop reorder (within a single specialty)
  const handleDoctorDragEnd = async (specialty, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Build current list of doctors in this specialty (visible/filtered)
    const inSp = data.doctors
      .filter(d => d.specialty === specialty)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const oldIndex = inSp.findIndex(d => d.id === active.id);
    const newIndex = inSp.findIndex(d => d.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(inSp, oldIndex, newIndex);
    const orderMap = new Map(reordered.map((d, i) => [d.id, i]));

    // Optimistic update — assign new order to docs in this specialty
    setData(prev => ({
      ...prev,
      doctors: prev.doctors.map(d =>
        d.specialty === specialty && orderMap.has(d.id)
          ? { ...d, order: orderMap.get(d.id) }
          : d
      ),
    }));

    try {
      await axios.post(
        `${API}/doctors/reorder`,
        { ids: reordered.map(d => d.id) },
        authHeaders,
      );
      toast.success("تم حفظ الترتيب");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل حفظ الترتيب");
      fetchAll();
    }
  };

  const openAdd = (specialty = "") => {
    setEditing(null);
    setForm({ name: "", role: "", exp: "", specialty });
    setDialogOpen(true);
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setForm({ name: doc.name, role: doc.role || "", exp: doc.exp || "", specialty: doc.specialty });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.specialty) {
      toast.error("الاسم والتخصص مطلوبان");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/doctors/${editing.id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("تم التحديث");
      } else {
        await axios.post(`${API}/doctors`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("تمت الإضافة");
      }
      setDialogOpen(false);
      fetchAll();
    } catch {
      toast.error("فشل في الحفظ");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`حذف "${doc.name}" من قسم "${doc.specialty}"؟`)) return;
    try {
      await axios.delete(`${API}/doctors/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم الحذف");
      fetchAll();
    } catch {
      toast.error("فشل في الحذف");
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    const filtered = data.doctors.filter(d =>
      !search ||
      d.name.includes(search) ||
      (d.role && d.role.includes(search)) ||
      d.specialty.includes(search)
    );
    for (const d of filtered) {
      if (!map[d.specialty]) map[d.specialty] = [];
      map[d.specialty].push(d);
    }
    // Sort each group by order
    for (const sp of Object.keys(map)) {
      map[sp].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return map;
  }, [data.doctors, search]);

  return (
    <div className="space-y-6" data-testid="doctors-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          {!embedded && (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">إدارة الأطباء</h1>
              <p className="text-slate-500 mt-1">
                أضف، عدّل، أو احذف الأطباء — اسحب لإعادة الترتيب — التغييرات تظهر فوراً في صفحة "أطباء المركز" للعملاء
              </p>
            </>
          )}
          {embedded && (
            <p className="text-sm text-slate-500">
              أضف، عدّل، أو احذف الأطباء — اسحب لإعادة الترتيب
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setSpDialogOpen(true)}
            variant="outline"
            className="border-[#655dc6]/40 text-[#655dc6] hover:bg-[#FAF9FE] gap-2"
            data-testid="manage-specialties-btn"
          >
            <Tags className="w-4 h-4" />
            إدارة الأقسام
          </Button>
          <Button
            onClick={() => openAdd()}
            className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2"
            data-testid="add-doctor-btn"
          >
            <Plus className="w-4 h-4" />
            إضافة طبيب جديد
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ابحث بالاسم أو التخصص أو المرتبة..."
              className="pr-10"
              data-testid="doctors-mgmt-search"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400">جاري التحميل...</div>
      ) : (
        data.specialties.map((sp) => (
          <Card key={sp} className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4 pb-3 border-b">
                <h3 className="font-bold text-lg text-[#655dc6]">
                  {sp}
                  <span className="text-xs font-normal text-slate-400 mr-2">
                    ({(grouped[sp] || []).length})
                  </span>
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAdd(sp)}
                  className="border-[#655dc6]/40 text-[#655dc6] hover:bg-[#FAF9FE] gap-1.5"
                  data-testid={`add-to-${sp}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  إضافة
                </Button>
              </div>

              {(grouped[sp] || []).length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-4">لا يوجد أطباء في هذا التخصص</p>
              ) : search ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {grouped[sp].map(doc => (
                    <SortableDoctorCard
                      key={doc.id}
                      doc={doc}
                      onEdit={() => openEdit(doc)}
                      onDelete={() => handleDelete(doc)}
                    />
                  ))}
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleDoctorDragEnd(sp, e)}
                >
                  <SortableContext
                    items={grouped[sp].map(d => d.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {grouped[sp].map(doc => (
                        <SortableDoctorCard
                          key={doc.id}
                          doc={doc}
                          onEdit={() => openEdit(doc)}
                          onDelete={() => handleDelete(doc)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              {editing ? "تعديل بيانات الطبيب" : "إضافة طبيب جديد"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">اسم الطبيب *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="د. الاسم الكامل"
                className="mt-1"
                data-testid="doctor-name-input"
              />
            </div>
            <div>
              <Label className="text-sm">المرتبة</Label>
              <Input
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
                placeholder="مثل: استشاري / أخصائي / طبيب عام"
                className="mt-1"
                data-testid="doctor-role-input"
              />
            </div>
            <div>
              <Label className="text-sm">سنوات الخبرة</Label>
              <Input
                value={form.exp}
                onChange={e => setForm({ ...form, exp: e.target.value })}
                placeholder="مثل: ١٠ سنوات (اختياري)"
                className="mt-1"
                data-testid="doctor-exp-input"
              />
            </div>
            <div>
              <Label className="text-sm">التخصص *</Label>
              <Select value={form.specialty} onValueChange={v => setForm({ ...form, specialty: v })}>
                <SelectTrigger className="mt-1" data-testid="doctor-specialty-select">
                  <SelectValue placeholder="اختر التخصص" />
                </SelectTrigger>
                <SelectContent>
                  {data.specialties.map(sp => (
                    <SelectItem key={sp} value={sp}>{sp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>إلغاء</Button>
            <Button
              className="bg-[#655dc6] hover:bg-[#5449a8] text-white"
              onClick={handleSave}
              data-testid="save-doctor-btn"
            >
              {editing ? "حفظ التعديل" : "إضافة"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manage Specialties Dialog */}
      <Dialog open={spDialogOpen} onOpenChange={(o) => { setSpDialogOpen(o); if (!o) cancelEditSp(); }}>
        <DialogContent dir="rtl" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Tags className="w-5 h-5" />
              إدارة الأقسام (التخصصات)
            </DialogTitle>
          </DialogHeader>

          {/* Add new */}
          <div className="flex gap-2 pt-2 pb-3 border-b">
            <Input
              value={newSpName}
              onChange={e => setNewSpName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") addSpecialty(); }}
              placeholder="اسم القسم الجديد..."
              data-testid="new-specialty-input"
            />
            <Button
              onClick={addSpecialty}
              className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5 shrink-0"
              data-testid="add-specialty-btn"
            >
              <Plus className="w-4 h-4" />
              إضافة
            </Button>
          </div>

          {/* List */}
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {specialtyDocs.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-6">لا توجد أقسام</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={specialtyDocs.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1.5">
                    {specialtyDocs.map(sp => {
                      const count = data.doctors.filter(d => d.specialty === sp.name).length;
                      const isEditing = editingSpId === sp.id;
                      return (
                        <SortableSpecialtyRow
                          key={sp.id}
                          sp={sp}
                          doctorCount={count}
                          isEditing={isEditing}
                          editingName={editingSpName}
                          setEditingName={setEditingSpName}
                          onStartEdit={() => startEditSp(sp)}
                          onSaveEdit={() => saveEditSp(sp)}
                          onCancelEdit={cancelEditSp}
                          onDelete={() => deleteSp(sp)}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          <p className="text-xs text-slate-400 text-center pt-1">
            اسحب وأفلت الأقسام لإعادة ترتيبها — سيتم حفظ التغيير تلقائياً
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSpDialogOpen(false)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorsManagement;
